import ProductStockSummary from '../components/ProductStockSummary';
import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsAPI, ficAPI, wmsAPI } from '../utils/api';
import { Prodotto, WmsConnectionStatus } from '../types';
import { useWmsStockBatch } from '../hooks/useWmsStock';
import { usePermissions } from '../hooks/usePermissions';
import { productDisplayPrice } from '../utils/productPrice';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Trash2,
  Package,
  Wrench,
  Tag,
  Loader2,
  DownloadCloud,
} from 'lucide-react';

export default function Products() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('prodotti:create');
  const canEdit = hasPermission('prodotti:edit');
  const canDelete = hasPermission('prodotti:delete');
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Number(searchParams.get('page') || 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const search = searchParams.get('search') || '';
  const categoriaFilter = searchParams.get('categoria') || '';
  const marcaFilter = searchParams.get('marca') || '';
  const tipoFilter = searchParams.get('tipo') || '';
  const statoFilter = searchParams.get('stato') || '';
  const updateFilter = (key: string, value: string) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    }, { replace: true });
  };
  const setPage = (value: number) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value > 1) next.set('page', String(value));
      else next.delete('page');
      return next;
    }, { replace: true });
  };
  const detailSearch = new URLSearchParams({ returnTo: `/products${searchParams.size ? `?${searchParams}` : ''}` }).toString();
  const listRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const scrollKey = `products-scroll:${searchParams.toString()}`;
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  // Fetch FIC status
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: async () => {
      const response = await ficAPI.getStatus();
      return response.data;
    },
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const response = await productsAPI.getCategories();
      return response.data;
    },
  });

  // Fetch brands
  const { data: brands } = useQuery({
    queryKey: ['product-brands'],
    queryFn: async () => {
      const response = await productsAPI.getBrands();
      return response.data;
    },
  });

  // Fetch products
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products', page, search, categoriaFilter, marcaFilter, tipoFilter, statoFilter],
    queryFn: async () => {
      const response = await productsAPI.getProducts({
        page,
        limit: 20,
        search: search || undefined,
        categoria: categoriaFilter || undefined,
        marca: marcaFilter || undefined,
        is_servizio: tipoFilter === '' ? undefined : tipoFilter === 'servizio',
        is_attivo: statoFilter === '' ? undefined : statoFilter === 'attivo',
      });
      return response.data;
    },
  });

  useLayoutEffect(() => {
    if (isLoading) return;
    const main = listRef.current?.closest('main');
    const table = tableScrollRef.current;
    if (!main || !table) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(scrollKey) || 'null');
      main.scrollTop = saved?.top || 0;
      table.scrollLeft = saved?.left || 0;
    } catch {
      main.scrollTop = 0;
      table.scrollLeft = 0;
    }
    const save = () => {
      try {
        sessionStorage.setItem(scrollKey, JSON.stringify({ top: main.scrollTop, left: table.scrollLeft }));
      } catch { /* Scroll restoration is optional when storage is unavailable. */ }
    };
    main.addEventListener('scroll', save, { passive: true });
    table.addEventListener('scroll', save, { passive: true });
    return () => {
      save();
      main.removeEventListener('scroll', save);
      table.removeEventListener('scroll', save);
    };
  }, [isLoading, scrollKey]);

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => productsAPI.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Prodotto eliminato con successo');
      setDeleteConfirm(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    },
  });

  // Import from FIC mutation
  const importMutation = useMutation({
    mutationFn: () => productsAPI.importFromFIC(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product-categories'] });
      toast.success(response.data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  // Fetch WMS status to know if integration is active
  const { data: wmsStatus } = useQuery<WmsConnectionStatus>({
    queryKey: ['wms-status'],
    queryFn: async () => {
      const response = await wmsAPI.getStatus();
      return response.data;
    },
  });

  const products = productsData?.data || [];
  const pagination = productsData?.pagination;

  const productIds = useMemo(() => products.map((p: Prodotto) => p.id), [products]);
  const { data: stockMap, isPending: loadingStock, isError: stockError } = useWmsStockBatch(wmsStatus?.is_active ? productIds : []);

  return (
    <div ref={listRef} className="min-w-0 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Prodotti e Servizi</h1>
          <p className="text-gray-600">Gestisci il catalogo prodotti e servizi</p>
        </div>
        <div className="flex gap-3">
          {ficStatus?.connected && (
            <button
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {importMutation.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <DownloadCloud className="h-5 w-5" />
              )}
              Importa da FIC
            </button>
          )}
          {canCreate && (
            <button
              onClick={() => navigate(`/products/new?${detailSearch}`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" />
              Nuovo Prodotto
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per nome, codice..."
              value={search}
              onChange={(e) => {
                updateFilter('search', e.target.value);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={categoriaFilter}
            onChange={(e) => {
              updateFilter('categoria', e.target.value);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutte le categorie</option>
            {categories?.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          <select
            value={marcaFilter}
            onChange={(e) => {
              updateFilter('marca', e.target.value);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutte le marche</option>
            {brands?.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
          <select
            value={tipoFilter}
            onChange={(e) => {
              updateFilter('tipo', e.target.value);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti i tipi</option>
            <option value="prodotto">Prodotti</option>
            <option value="servizio">Servizi</option>
          </select>
          <select
            value={statoFilter}
            onChange={(e) => {
              updateFilter('stato', e.target.value);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="attivo">Attivi</option>
            <option value="inattivo">Inattivi</option>
          </select>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun prodotto trovato</p>
          </div>
        ) : (
          <div ref={tableScrollRef} className="w-full overflow-x-auto" role="region" aria-label="Elenco prodotti" tabIndex={0}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prodotto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marca
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prezzo
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IVA
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Margine
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato
                </th>
                {wmsStatus?.is_active && (
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Disp.
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product: Prodotto) => {
                const price = productDisplayPrice(product);
                return (
                <tr
                  key={product.id}
                  onClick={canEdit ? () => navigate(`/products/${product.id}/edit?${detailSearch}`) : undefined}
                  className={canEdit ? 'hover:bg-gray-50 cursor-pointer' : ''}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {product.immagine_default_url ? (
                          <img
                            src={product.immagine_default_url}
                            alt={product.nome}
                            className="max-h-full max-w-full h-auto w-auto object-contain"
                            loading="lazy"
                          />
                        ) : product.is_servizio ? (
                          <Wrench className="h-5 w-5 text-purple-400" />
                        ) : (
                          <Package className="h-5 w-5 text-blue-400" />
                        )}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {product.nome}
                        </div>
                        <div className="text-sm text-gray-500">
                          {product.codice && `Cod: ${product.codice}`}
                          {product.codice && product.unita_misura && ' · '}
                          {product.unita_misura && `UM: ${product.unita_misura}`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.categoria && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <Tag className="h-3 w-3 mr-1" />
                        {product.categoria}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.marca && (
                      <span className="text-sm text-gray-700">
                        {product.marca}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {price.amount !== null
                        ? formatCurrency(price.amount)
                        : 'Prezzo non configurato'}
                    </div>
                    {price.amount !== null && (
                      <div className="text-xs text-gray-500">{price.source}</div>
                    )}
                    {product.prezzo_acquisto !== undefined && product.prezzo_acquisto !== null && (
                      <div className="text-xs text-gray-500">
                        Costo: {formatCurrency(product.prezzo_acquisto)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {product.aliquota_iva}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {product.margine > 0 ? (
                      <span className={`text-sm font-medium ${product.margine >= 30 ? 'text-green-600' : product.margine >= 15 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {product.margine.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {product.is_attivo ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Attivo
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inattivo
                      </span>
                    )}
                  </td>
                  {wmsStatus?.is_active && (
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {product.is_servizio ? <span className="text-gray-400">—</span> : loadingStock ? (
                        <span className="text-xs text-gray-500">Caricamento…</span>
                      ) : stockError ? (
                        <span className="text-xs text-gray-500">Giacenza non disponibile</span>
                      ) : (
                        <ProductStockSummary stock={stockMap?.get(product.id)} unit={product.unita_misura || 'pz'} />
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(product.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} prodotti)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Conferma Eliminazione</h3>
            <p className="text-gray-600 mb-6">
              Sei sicuro di voler eliminare questo prodotto? L'operazione non può essere annullata.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Elimina'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
